import { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config/api";
import { getToken } from "../data/storage/authStorage";
import { rememberLocationGstDates } from "../utils/gstApplicability";

/** ZOUR-65 — one of the company's GST registrations, as a picker needs it. */
export interface ActiveLocation {
  /** Zoho's id. Null only on the synthetic row of a tenant that never synced. */
  zoho_location_id: string | null;
  name: string;
  gstin: string | null;
  state: string | null;
  /** Always compare on this, never on `state` — see the ZOUR-65 P1 notes. */
  state_code: string | null;
  is_primary: boolean;
  /**
   * ZOUR-92 — the date GST starts applying to documents filed under this
   * registration, ALREADY RESOLVED by the server: the registration's own date
   * when it has set one, the company's otherwise.
   *
   * The browser must not do that fallback itself. It holds the company date in
   * localStorage and has no idea a branch might differ, which is exactly how a
   * pre-registration branch bill would have been drawn with tax on screen while
   * the push stripped it.
   *
   * Optional because a tenant whose backend predates ZOUR-92 does not send it,
   * and the twin then falls back to the company date — today's behaviour.
   */
  gst_enabled_from?: string | null;
}

export interface UseActiveLocationsReturn {
  locations: ActiveLocation[];
  /** False for every single-registration company — the picker stays hidden. */
  hasMultiple: boolean;
  loading: boolean;
  /** The registration a document falls to when nobody chooses. */
  primary: ActiveLocation | null;
}

/**
 * ZOUR-65 phase 2 — the locations a document can be filed against.
 *
 * Backed by `GET /api/v1/locations/active`, which is open to any signed-in
 * user: the people filing documents are accountants, not admins, and the
 * settings read stays admin-only.
 *
 * `hasMultiple` is the flag every caller checks. It is false for every company
 * with one GST registration, which is almost all of them — and for those the
 * picker must not appear at all, so phase 2 is invisible to them.
 *
 * FAILS QUIET. A review page must still work if this call fails: the result is
 * an empty list, `hasMultiple` false, no picker, and the document pushes with
 * no location — which is exactly the pre-ZOUR-65 behaviour, and Zoho files it
 * under the primary location anyway.
 */
export const useActiveLocations = (): UseActiveLocationsReturn => {
  const [locations, setLocations] = useState<ActiveLocation[]>([]);
  const [hasMultiple, setHasMultiple] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const token = getToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/v1/locations/active`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        const body = await response.json();

        if (controller.signal.aborted) return;

        if (response.ok && body?.success && body?.data) {
          const rows = (body.data.locations ?? []) as ActiveLocation[];
          setLocations(rows);
          setHasMultiple(body.data.has_multiple === true);
          // ZOUR-92: hand the per-registration dates to the pure GST twin, so
          // the functions the pages already call can answer per document
          // without every one of them threading a locations list through.
          rememberLocationGstDates(rows);
        }
      } catch {
        // Deliberately quiet — see the hook comment.
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => controller.abort();
  }, []);

  return {
    locations,
    hasMultiple,
    loading,
    primary: locations.find((location) => location.is_primary) ?? locations[0] ?? null,
  };
};
